import { ZfBanner, ZfBox, ZfLoadingSpinner } from '@zafire/react';
import React from 'react';
import { PartnerSearchByBrResponse, ServiceAgreementV2 } from 'pco-product-service-internal-consumer';
import type { SaveState } from '../../utils/saveGate';
import { usePartnerSelectionSection } from '../hooks/usePartnerSelectionSection';
import { PartnerSearchWidget } from './PartnerSearchWidget';
import type { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

type Props = {
  serviceAgreement: ServiceAgreementV2;
  brUid: string;
  contactNoteUids?: string[];
  allowAllPartnersSearch: boolean;
  extraPartnerSearchColumns: ColDef<PartnerSearchByBrResponse>[];
  onSaveStateChange: (state: SaveState) => void;
};

export const PartnerSelectionSection: React.FC<Props> = ({
  serviceAgreement,
  brUid,
  contactNoteUids,
  allowAllPartnersSearch,
  extraPartnerSearchColumns,
  onSaveStateChange,
}) => {
  const { t } = useTranslation();
  const {
    isServiceAgreementDetailsFetching,
    isPartnerSelectionDisabled,
    selectedPartners,
    setSelectedPartners,
    showProductClosureWarning,
  } = usePartnerSelectionSection({ serviceAgreement, contactNoteUids, onSaveStateChange });

  return (
    <>
      {showProductClosureWarning && (
        <ZfBox style={{ marginBottom: 'var(--zf-spacing-small, 12px)' }}>
          <ZfBanner
            data-testid="product-closure-warning"
            type="warning"
            titleText={t('pb.configureproductmodal.productclosurewarning.title', 'Product closure')}
            description={t(
              'pb.configureproductmodal.productclosurewarning.description',
              'Removing all partners will result in the product being closed',
            )}
            dismissible={false}
          />
        </ZfBox>
      )}
      {isServiceAgreementDetailsFetching && (
        <ZfBox
          data-testid="service-roles-loading"
          style={{ display: 'flex', justifyContent: 'center', padding: 'var(--zf-spacing-medium, 16px)' }}
        >
          <ZfLoadingSpinner size="medium" />
        </ZfBox>
      )}
      <PartnerSearchWidget
        brUid={brUid}
        selectedPartners={selectedPartners}
        onSelectedPartnersChange={setSelectedPartners}
        disabled={isPartnerSelectionDisabled}
        allowAllPartnersSearch={allowAllPartnersSearch}
        extraColumns={extraPartnerSearchColumns}
        serviceDefinitionId={serviceAgreement.serviceDefinitionId}
        productName={serviceAgreement.serviceDefinitionName}
      />
    </>
  );
};
